// Turns an encoded audio file into a small array of peaks the waveform can draw (#122).
//
// Decoding happens in an OfflineAudioContext pinned to 8 kHz mono: browsers resample while
// they decode, so a 20 MB / 20 minute upload costs ~38 MB of Float32 instead of the ~400 MB
// a 44.1 kHz stereo decode would hold. The samples are thrown away as soon as the peaks are
// bucketed - only PEAK_BUCKETS floats survive the call.

// Enough resolution to redraw at any width we render at (the canvas aggregates buckets down
// to its own pixel count) without keeping the decoded track around.
const PEAK_BUCKETS = 2048
const DECODE_SAMPLE_RATE = 8000

export async function extractPeaks(arrayBuffer) {
  const OfflineContext = window.OfflineAudioContext || window.webkitOfflineAudioContext
  if (!OfflineContext) throw new Error("no Web Audio support")

  // length has to be at least 1 frame; nothing is ever rendered through this context.
  const context = new OfflineContext(1, 1, DECODE_SAMPLE_RATE)
  const buffer = await decode(context, arrayBuffer)

  return { peaks: bucketPeaks(buffer), duration: buffer.duration }
}

// Safari only grew the promise form of decodeAudioData late, and still wants the callbacks.
function decode(context, arrayBuffer) {
  return new Promise((resolve, reject) => {
    const pending = context.decodeAudioData(arrayBuffer, resolve, reject)
    if (pending && typeof pending.then === "function") pending.then(resolve, reject)
  })
}

// Peak (not RMS) per bucket: what a band looks for is where the track gets loud, and peaks
// keep the chorus visibly taller than the verse. Normalised so a quietly mastered track
// fills the same height as a loud one.
function bucketPeaks(buffer) {
  const channels = []
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    channels.push(buffer.getChannelData(channel))
  }

  const peaks = new Float32Array(PEAK_BUCKETS)
  const samplesPerBucket = buffer.length / PEAK_BUCKETS
  let loudest = 0

  for (let bucket = 0; bucket < PEAK_BUCKETS; bucket++) {
    const from = Math.floor(bucket * samplesPerBucket)
    const to = Math.min(buffer.length, Math.floor((bucket + 1) * samplesPerBucket))
    let peak = 0

    for (const samples of channels) {
      for (let index = from; index < to; index++) {
        const level = Math.abs(samples[index])
        if (level > peak) peak = level
      }
    }

    peaks[bucket] = peak
    if (peak > loudest) loudest = peak
  }

  if (loudest > 0) {
    for (let bucket = 0; bucket < PEAK_BUCKETS; bucket++) peaks[bucket] /= loudest
  }

  return peaks
}
